// Pure deriver: maps a live `supplier_contract_documents` doc (+ a little
// in-flight UI runtime) into the `{ stages }` shape the shared Timeline
// component renders. No React, no side effects — call it on every render.
//
// Stages:
//   1. Classify  — parseSupplierDocument (Agent #1). "Sent to classifier" flips
//      done off the server's own CLASSIFYING marker; a badge shows the routing
//      decision (which specialist we'd call next).
//   2. Specialist extraction (parallel group) — appears once the orchestrator
//      has written `processing_steps`; one sub-card per specialist that ran
//      (accommodation / green fees / packages), bound to its live status.
//   3. Build search hooks — the indexer step.
// Stages 2-3 render purely from the live `processing_steps` snapshot, so they
// animate as the server-side pipeline progresses (the modal re-renders on each
// Firestore update).

const RATE_CARD = "supplier.rate_card"

const CLASS_LABELS = {
	"supplier.rate_card": "Rate card",
	"supplier.contract": "Contract",
	"supplier.discount_sales": "Discount / promotion",
	"supplier.stop_sales": "Stop sale",
	"supplier.information": "Supplier information",
}

const TYPE_READER_LABELS = {
	"supplier.contract": "Contract reader (coming soon)",
	"supplier.discount_sales": "Promotion reader (coming soon)",
	"supplier.stop_sales": "Stop-sale reader (coming soon)",
	"supplier.information": "Supplier-info reader (coming soon)",
}

const classLabel = (value) => CLASS_LABELS[value] || "Unclassified"

const pct = (score) => (typeof score === "number" ? `${Math.round(score * 100)}%` : null)

const getSeconds = (ts) => {
	if (!ts) return null
	if (typeof ts._seconds === "number") return ts._seconds
	if (typeof ts.seconds === "number") return ts.seconds
	return null
}

const cap = (s) => (typeof s === "string" && s ? s.charAt(0).toUpperCase() + s.slice(1) : "")

// Compact chips for the document's valid_ranges (start -> end, tagged by kind).
const rangeChips = (doc) => {
	const ranges = Array.isArray(doc.valid_ranges) ? doc.valid_ranges : []
	return ranges
		.filter((r) => r && r.start)
		.map((r) => (r.end ? `${cap(r.kind)} ${r.start} → ${r.end}` : `${cap(r.kind)} from ${r.start}`))
}

// Which specialist channel would we route to next, given the classification?
// Mirrors the orchestrator: a rate card fans out to accommodation/green-fee
// readers depending on the referenced entities; everything else maps to its
// (not-yet-built) reader.
const specialistDecision = (doc, effClass) => {
	if (!effClass) return "No specialist - could not classify"
	if (effClass === RATE_CARD) {
		const hotels = doc.referenced_hotels?.length || 0
		const courses = doc.referenced_courses?.length || 0
		const readers = []
		if (hotels > 0) readers.push("Accommodation reader")
		if (courses > 0) readers.push("Green-fees reader")
		if (readers.length === 0) return "No specialist needed"
		return `Next: ${readers.join(" + ")}`
	}
	return `Next: ${TYPE_READER_LABELS[effClass] || "Specialist reader (coming soon)"}`
}

// Map a backend processing_steps status onto a timeline status.
const tlStatus = (st) => {
	switch (st) {
		case "completed":
			return "complete"
		case "in_progress":
			return "active"
		case "failed":
			return "failed"
		case "skipped":
			return "skipped"
		default:
			return "pending"
	}
}

// Resolve a parallel group's status from its sub-cards.
const groupStatusFrom = (subs) => {
	if (subs.some((s) => s.status === "active")) return "active"
	const settled = subs.every((s) => ["complete", "skipped", "failed"].includes(s.status))
	if (settled) {
		if (subs.some((s) => s.status === "complete")) return "complete"
		if (subs.some((s) => s.status === "failed")) return "failed"
		return "skipped"
	}
	return "pending"
}

/**
 * @param doc      live supplier_contract_documents data (may be partial)
 * @param runtime  { phase, classification, error }
 *                 phase: "classifying" | "done" | "error"
 */
export function buildSupplierTimeline(doc = {}, runtime = {}) {
	const { phase = "done", classification: runtimeClass = null, error = null } = runtime

	const effClass = runtimeClass || doc.classification || null
	const inFlight = phase === "classifying"
	const failed = phase === "error"
	const classified = !inFlight && !failed && (!!effClass || !!doc.parsed_at)

	const stageStatus = inFlight ? "active" : failed ? "failed" : classified ? "complete" : "pending"

	const confidence = pct(doc.agent_metadata?.confidence_score)
	const docName = doc.filename || doc.email_subject || null

	// True server-side confirmation: the function stamps status "CLASSIFYING"
	// before the model call, so this tick means the server has actually started
	// — not merely that the browser fired the request.
	const serverStarted = classified || doc.status === "CLASSIFYING"
	const sentStatus = serverStarted ? "complete" : failed ? "failed" : "active"
	const resultStatus = classified ? "complete" : failed ? "failed" : "active"

	// Routing badge: waits on the classifier, then names the specialist we'd
	// call next.
	let routingBadge
	if (classified) {
		routingBadge = { icon: "check", label: specialistDecision(doc, effClass), variant: "success" }
	} else if (failed) {
		routingBadge = { label: "No classification", variant: "pending" }
	} else {
		routingBadge = { label: "Waiting for classification", variant: "loading" }
	}

	const rangeSummaryChips = rangeChips(doc)

	const classifyStage = {
		id: "classify",
		header: {
			icon: "file-search",
			title: docName ? `Classifying document '${docName}'` : "Classify document",
		},
		trigger: routingBadge,
		triggerAfter: true,
		activity: {
			status: stageStatus,
			title: "Read and classify document",
			summary: classified
				? [
						effClass ? `${classLabel(effClass)} (${effClass})` : classLabel(effClass),
						confidence ? `${confidence} confidence` : null,
						...rangeSummaryChips.slice(0, 4),
						rangeSummaryChips.length > 4 ? `+${rangeSummaryChips.length - 4} more ranges` : null,
				  ].filter(Boolean)
				: null,
			error: failed ? error : null,
			items: [
				{
					status: sentStatus,
					content: "Sent to classifier",
					sub: serverStarted ? "On its way to classifying agent" : undefined,
				},
				{
					status: resultStatus,
					content: "Classify against known enums",
					sub: classified ? classLabel(effClass) : undefined,
				},
				{
					status: resultStatus,
					content: "Score confidence",
					sub: classified
						? confidence
							? `Confidence ${confidence}`
							: "Confidence not reported"
						: undefined,
				},
			],
		},
	}

	/* ---- Specialist extraction + index, live from processing_steps ---- */
	const ps = doc.processing_steps || {}
	const hasPipeline = !!(ps.accommodation || ps.green_fees || ps.packages || ps.index)

	// Only show the specialist + index stages for the CURRENT run. The
	// orchestrator re-initialises processing_steps after each parse, stamping
	// classify.started_at with the new parsed_at; a prior run's (stale) steps
	// carry an older started_at, so we ignore them until the new run lands -
	// otherwise the cards flash their previous results before classifying. We
	// also suppress while the classify call is still in flight.
	const parsedAtSec = getSeconds(doc.parsed_at)
	const classifyStartedSec = getSeconds(ps.classify && ps.classify.started_at)
	const pipelineFresh =
		!inFlight &&
		classifyStartedSec !== null &&
		parsedAtSec !== null &&
		classifyStartedSec >= parsedAtSec
	const showPipeline = pipelineFresh && hasPipeline

	const specialistSub = (key, title, lines, supplements, conf) => {
		const st = tlStatus(ps[key] && ps[key].status)
		let summary = null
		if (st === "complete") {
			summary =
				lines === 0 && supplements === 0
					? "No rates found"
					: [
							`${lines} line${lines === 1 ? "" : "s"}`,
							`${supplements} supplement${supplements === 1 ? "" : "s"}`,
							conf ? `${conf} confidence` : null,
					  ].filter(Boolean)
		} else if (st === "skipped") {
			summary = "Not in this document"
		}
		return {
			id: key,
			status: st,
			title,
			summary,
			error: st === "failed" ? (ps[key] && ps[key].error) || "Extraction failed" : null,
			items: [],
		}
	}

	const subs = []
	if (ps.accommodation) {
		subs.push(
			specialistSub(
				"accommodation",
				"Accommodation reader",
				doc.accommodation_lines?.length || 0,
				doc.accommodation_supplements?.length || 0,
				pct(doc.accommodation_agent_metadata?.confidence_score)
			)
		)
	}
	if (ps.green_fees) {
		subs.push(
			specialistSub(
				"green_fees",
				"Green fees reader",
				doc.green_fee_lines?.length || 0,
				doc.green_fee_supplements?.length || 0,
				pct(doc.green_fees_agent_metadata?.confidence_score)
			)
		)
	}
	if (ps.packages) {
		subs.push(
			specialistSub(
				"packages",
				"Packages reader",
				doc.package_lines?.length || 0,
				doc.package_supplements?.length || 0,
				pct(doc.packages_agent_metadata?.confidence_score)
			)
		)
	}

	const extractStage =
		showPipeline && subs.length > 0
			? {
					id: "extract",
					header: { icon: "files", title: "Hand off to specialist agents" },
					trigger: { icon: "zap", label: "Specialist agents" },
					group: {
						status: groupStatusFrom(subs),
						title: "Reading rates in parallel",
						activities: subs,
					},
			  }
			: null

	let indexSummary = null
	if (tlStatus(ps.index && ps.index.status) === "complete") {
		const parts = []
		const months = doc.months_available?.length || 0
		const rooms = doc.room_types_indexed?.length || 0
		const courses = doc.courses_indexed?.length || 0
		const currencies = doc.currencies_seen || []
		if (months > 0) parts.push(`${months} month${months === 1 ? "" : "s"}`)
		if (rooms > 0) parts.push(`${rooms} room type${rooms === 1 ? "" : "s"}`)
		if (courses > 0) parts.push(`${courses} course${courses === 1 ? "" : "s"}`)
		if (currencies.length > 0) parts.push(currencies.join(", "))
		indexSummary = parts.length > 0 ? parts : "Search hooks built"
	}

	const indexStage =
		showPipeline && ps.index
			? {
					id: "index",
					header: { icon: "database", title: "Build search hooks" },
					activity: {
						status: tlStatus(ps.index && ps.index.status),
						title: "Index for search",
						summary: indexSummary,
						items: [],
					},
			  }
			: null

	return { stages: [classifyStage, extractStage, indexStage].filter(Boolean) }
}
